import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { GRID_SETTINGS_CUSTOM_SORT_ID } from '../../constants';
import type { GridSettingsDraft, GridSettingsPreference, GridSettingsSortField } from '../../types';
import {
    groupedFieldsForFields,
    groupedFieldsWithToggle,
    hasCustomConfiguration,
    isCustomSortId,
    toGridSettingsDraft,
    toGridSettingsPreference,
    toOptionId,
    toOptionIds,
    valueOrdersForFields
} from '../utils';

type PendingSortFieldRemoval = {
    nextSortFields: string[];
};

type UseGridSettingsModalParams = {
    open: boolean;
    sortFields: GridSettingsSortField[];
    preference: GridSettingsPreference;
    autosizeEnabled: boolean;
    onSave: (preference: GridSettingsPreference, autosizeEnabled: boolean) => void;
};

export function useGridSettingsModal({
    open,
    sortFields,
    preference,
    autosizeEnabled,
    onSave
}: UseGridSettingsModalParams) {
    const [confirmAutosizeOpen, setConfirmAutosizeOpen] = useState(false);
    const [customRemovalPending, setCustomRemovalPending] = useState(false);
    const [pendingSortFieldRemoval, setPendingSortFieldRemoval] = useState<PendingSortFieldRemoval | null>(null);
    const sortFieldsRef = useRef(sortFields);
    sortFieldsRef.current = sortFields;
    const [draft, setDraft] = useState<GridSettingsDraft>(() =>
        toGridSettingsDraft(preference, autosizeEnabled, sortFields)
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        setDraft(toGridSettingsDraft(preference, autosizeEnabled, sortFieldsRef.current));
        setCustomRemovalPending(false);
        setPendingSortFieldRemoval(null);
    }, [autosizeEnabled, open, preference]);

    const handleAutosizeChange = useCallback(
        (_event: unknown, checked: boolean) => {
            if (checked && !autosizeEnabled) {
                setConfirmAutosizeOpen(true);
                return;
            }

            setDraft((prev) => ({ ...prev, autosizeEnabled: checked }));
        },
        [autosizeEnabled]
    );

    const handleConfirmAutosize = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setConfirmAutosizeOpen(false);
        setDraft((prev) => ({ ...prev, autosizeEnabled: true }));
    }, []);

    const handleCancelAutosize = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setConfirmAutosizeOpen(false);
    }, []);

    const handleSortByChange = useCallback(
        (_name: string, selectedValue: unknown) => {
            const nextSortBy = toOptionId(selectedValue);

            if (!nextSortBy) {
                return;
            }

            if (isCustomSortId(draft.sortBy) && !isCustomSortId(nextSortBy) && hasCustomConfiguration(draft)) {
                setDraft((prev) => ({ ...prev, sortBy: nextSortBy }));
                setCustomRemovalPending(true);
                return;
            }

            setDraft((prev) => ({ ...prev, sortBy: nextSortBy }));
            setCustomRemovalPending(false);
        },
        [draft]
    );

    const handleSortFieldsChange = useCallback(
        (_name: string, selectedValue: unknown) => {
            const nextSortFields = toOptionIds(selectedValue);
            const removedFieldId = draft.sortFields.find(
                (fieldId) => !nextSortFields.includes(fieldId) && (draft.valueOrders[fieldId] || []).length > 0
            );

            if (removedFieldId) {
                setPendingSortFieldRemoval({ nextSortFields });
                return;
            }

            setDraft((prev) => ({
                ...prev,
                sortFields: nextSortFields,
                valueOrders: valueOrdersForFields(prev.valueOrders, nextSortFields),
                groupedFields: groupedFieldsForFields(prev.groupedFields, nextSortFields)
            }));
        },
        [draft.sortFields, draft.valueOrders]
    );

    const handleValueOrderChange = useCallback((fieldId: string, values: string[]) => {
        setDraft((prev) => ({
            ...prev,
            valueOrders: {
                ...prev.valueOrders,
                [fieldId]: values
            }
        }));
    }, []);

    const handleInGroupSortChange = useCallback((_name: string, selectedValue: string | null) => {
        setDraft((prev) => ({ ...prev, inGroupSortBy: selectedValue }));
    }, []);

    const handleGroupedFieldChange = useCallback((fieldId: string, checked: boolean) => {
        setDraft((prev) => ({
            ...prev,
            groupedFields: groupedFieldsWithToggle(prev.sortFields, prev.groupedFields, fieldId, checked)
        }));
    }, []);

    const handleConfirmCustomRemoval = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setDraft((prev) => ({
            ...prev,
            sortFields: [],
            valueOrders: {},
            groupedFields: [],
            inGroupSortBy: null
        }));
        setCustomRemovalPending(false);
    }, []);

    const handleCancelCustomRemoval = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setDraft((prev) => ({ ...prev, sortBy: GRID_SETTINGS_CUSTOM_SORT_ID }));
        setCustomRemovalPending(false);
    }, []);

    const handleConfirmSortFieldRemoval = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (!pendingSortFieldRemoval) {
                return;
            }

            const { nextSortFields } = pendingSortFieldRemoval;
            setDraft((prev) => ({
                ...prev,
                sortFields: nextSortFields,
                valueOrders: valueOrdersForFields(prev.valueOrders, nextSortFields),
                groupedFields: groupedFieldsForFields(prev.groupedFields, nextSortFields)
            }));
            setPendingSortFieldRemoval(null);
        },
        [pendingSortFieldRemoval]
    );

    const handleCancelSortFieldRemoval = useCallback((event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setPendingSortFieldRemoval(null);
    }, []);

    const handleSave = useCallback(() => {
        onSave(toGridSettingsPreference(draft), draft.autosizeEnabled);
    }, [draft, onSave]);

    return {
        draft,
        confirmAutosizeOpen,
        customRemovalPending,
        pendingSortFieldRemoval: pendingSortFieldRemoval != null,
        handleAutosizeChange,
        handleConfirmAutosize,
        handleCancelAutosize,
        handleSortByChange,
        handleSortFieldsChange,
        handleValueOrderChange,
        handleInGroupSortChange,
        handleGroupedFieldChange,
        handleConfirmCustomRemoval,
        handleCancelCustomRemoval,
        handleConfirmSortFieldRemoval,
        handleCancelSortFieldRemoval,
        handleSave
    };
}
